import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Trophy, AlertTriangle, CalendarDays, Filter, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface Props {
  poolId: string;
  lotteryId: string;
  lotterySlug: string;
}

type PoolGame = { id: string; position: number; numbers: number[] };
type Draw = { id: string; contest_number: number; draw_date: string | null };
type DrawNumber = { draw_id: string; number: number };

/** Faixas de prêmio oficiais por loteria — espelha compute_prize_tier no banco. */
function prizeTier(slug: string, hits: number): number | null {
  if (slug === "mega-sena") {
    if (hits === 6) return 6;
    if (hits === 5) return 5;
    if (hits === 4) return 4;
  } else if (slug === "lotofacil") {
    if (hits >= 11 && hits <= 15) return hits;
  } else if (slug === "quina") {
    if (hits >= 2 && hits <= 5) return hits;
  } else if (slug === "dupla-sena") {
    if (hits >= 3 && hits <= 6) return hits;
  }
  return null;
}

function tierLabel(slug: string, tier: number): string {
  if (slug === "mega-sena") {
    if (tier === 6) return "Sena";
    if (tier === 5) return "Quina";
    if (tier === 4) return "Quadra";
  }
  if (slug === "quina") {
    if (tier === 5) return "Quina";
    if (tier === 4) return "Quadra";
    if (tier === 3) return "Terno";
    if (tier === 2) return "Duque";
  }
  if (slug === "lotofacil") return `${tier} acertos`;
  if (slug === "dupla-sena") {
    if (tier === 6) return "Sena";
    if (tier === 5) return "Quina";
    if (tier === 4) return "Quadra";
    if (tier === 3) return "Terno";
  }
  return `${tier}`;
}

export function PoolPrizesPanel({ poolId, lotteryId, lotterySlug }: Props) {
  const { data: games } = useQuery({
    queryKey: ["pool-games-prizes", poolId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pool_games")
        .select("id, position, numbers")
        .eq("pool_id", poolId)
        .order("position");
      if (error) throw error;
      return (data ?? []) as PoolGame[];
    },
  });

  const { data: draws } = useQuery({
    queryKey: ["pool-draws", lotteryId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("draws")
        .select("id, contest_number, draw_date")
        .eq("lottery_id", lotteryId)
        .order("contest_number", { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []) as Draw[];
    },
  });

  const drawIds = useMemo(() => (draws ?? []).map((d) => d.id), [draws]);

  const { data: drawNumbers, isLoading: loadingNumbers } = useQuery({
    queryKey: ["pool-draw-numbers", drawIds],
    enabled: drawIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("draw_numbers")
        .select("draw_id, number")
        .in("draw_id", drawIds);
      if (error) throw error;
      return (data ?? []) as DrawNumber[];
    },
  });

  const isLoading = !games || !draws || loadingNumbers;

  // Limites disponíveis (com base nos concursos retornados)
  const availableRange = useMemo(() => {
    if (!draws || draws.length === 0) return null;
    const nums = draws.map((d) => d.contest_number);
    return { min: Math.min(...nums), max: Math.max(...nums) };
  }, [draws]);

  const storageKey = `lt-pool-prizes-filter:${poolId}`;

  type Preset = 10 | 30 | 50 | "all" | null;

  const [minContest, setMinContest] = useState<string>("");
  const [maxContest, setMaxContest] = useState<string>("");
  const [activePreset, setActivePreset] = useState<Preset>(null);
  const [hydrated, setHydrated] = useState(false);

  // Carrega filtros persistidos por bolão
  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw) as { min?: string; max?: string; preset?: Preset };
        if (typeof parsed.min === "string") setMinContest(parsed.min);
        if (typeof parsed.max === "string") setMaxContest(parsed.max);
        if (parsed.preset === 10 || parsed.preset === 30 || parsed.preset === 50 || parsed.preset === "all") {
          setActivePreset(parsed.preset);
        }
      }
    } catch {
      // ignora JSON inválido
    }
    setHydrated(true);
  }, [storageKey]);

  // Salva filtros sempre que mudam (após hidratação)
  useEffect(() => {
    if (!hydrated) return;
    try {
      if (!minContest && !maxContest && !activePreset) {
        localStorage.removeItem(storageKey);
      } else {
        localStorage.setItem(
          storageKey,
          JSON.stringify({ min: minContest, max: maxContest, preset: activePreset }),
        );
      }
    } catch {
      // storage pode estar cheio/bloqueado
    }
  }, [minContest, maxContest, activePreset, hydrated, storageKey]);

  // Reaplica preset sempre que a faixa disponível muda (ex.: novo concurso)
  useEffect(() => {
    if (!hydrated || !availableRange || !activePreset) return;
    if (activePreset === "all") {
      if (minContest !== "" || maxContest !== "") {
        setMinContest("");
        setMaxContest("");
      }
      return;
    }
    const newMin = String(Math.max(availableRange.min, availableRange.max - activePreset + 1));
    const newMax = String(availableRange.max);
    if (newMin !== minContest) setMinContest(newMin);
    if (newMax !== maxContest) setMaxContest(newMax);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated, availableRange?.min, availableRange?.max, activePreset]);

  const applyPreset = (lastN: number | "all") => {
    if (!availableRange) return;
    setActivePreset(lastN as Preset);
    if (lastN === "all") {
      setMinContest("");
      setMaxContest("");
      return;
    }
    setMinContest(String(Math.max(availableRange.min, availableRange.max - lastN + 1)));
    setMaxContest(String(availableRange.max));
  };

  const handleMinChange = (v: string) => {
    setMinContest(v);
    setActivePreset(null);
  };
  const handleMaxChange = (v: string) => {
    setMaxContest(v);
    setActivePreset(null);
  };

  const clearFilters = () => {
    setMinContest("");
    setMaxContest("");
    setActivePreset(null);
  };

  const minNum = minContest ? Number(minContest) : null;
  const maxNum = maxContest ? Number(maxContest) : null;
  const hasFilters = minNum != null || maxNum != null;

  // Agrupa números por concurso
  const drawsByContest = useMemo(() => {
    if (!draws || !drawNumbers) return [];
    const numsByDraw = new Map<string, Set<number>>();
    drawNumbers.forEach((dn) => {
      if (!numsByDraw.has(dn.draw_id)) numsByDraw.set(dn.draw_id, new Set());
      numsByDraw.get(dn.draw_id)!.add(dn.number);
    });
    return draws.map((d) => ({
      ...d,
      drawn: numsByDraw.get(d.id) ?? new Set<number>(),
    }));
  }, [draws, drawNumbers]);

  // Aplica filtro de intervalo de concursos
  const filteredDraws = useMemo(() => {
    return drawsByContest.filter((d) => {
      if (minNum != null && d.contest_number < minNum) return false;
      if (maxNum != null && d.contest_number > maxNum) return false;
      return true;
    });
  }, [drawsByContest, minNum, maxNum]);

  // Calcula prêmios por concurso
  const prizeContests = useMemo(() => {
    if (!games) return [];
    const out: Array<{
      contest_number: number;
      draw_date: string | null;
      drawn: number[];
      winners: Array<{
        position: number;
        numbers: number[];
        matched: number[];
        hits: number;
        tier: number;
      }>;
    }> = [];

    for (const draw of filteredDraws) {
      if (draw.drawn.size === 0) continue;
      const winners: typeof out[number]["winners"] = [];
      for (const g of games) {
        const matched = g.numbers.filter((n) => draw.drawn.has(n));
        const tier = prizeTier(lotterySlug, matched.length);
        if (tier != null) {
          winners.push({
            position: g.position,
            numbers: g.numbers,
            matched,
            hits: matched.length,
            tier,
          });
        }
      }
      if (winners.length > 0) {
        winners.sort((a, b) => b.hits - a.hits || a.position - b.position);
        out.push({
          contest_number: draw.contest_number,
          draw_date: draw.draw_date,
          drawn: Array.from(draw.drawn).sort((a, b) => a - b),
          winners,
        });
      }
    }
    return out;
  }, [games, filteredDraws, lotterySlug]);

  if (isLoading) {
    return <Skeleton className="h-64" />;
  }

  const totalWinners = prizeContests.reduce((s, c) => s + c.winners.length, 0);

  const filtersUI = (
    <Card>
      <CardContent className="space-y-3 pt-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
          <Filter className="h-3.5 w-3.5" /> Filtrar concursos
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label htmlFor="min-contest" className="text-[11px] text-muted-foreground">
              Concurso inicial
            </Label>
            <Input
              id="min-contest"
              type="number"
              inputMode="numeric"
              placeholder={availableRange ? String(availableRange.min) : ""}
              value={minContest}
              onChange={(e) => handleMinChange(e.target.value)}
              className="h-9 w-32"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="max-contest" className="text-[11px] text-muted-foreground">
              Concurso final
            </Label>
            <Input
              id="max-contest"
              type="number"
              inputMode="numeric"
              placeholder={availableRange ? String(availableRange.max) : ""}
              value={maxContest}
              onChange={(e) => handleMaxChange(e.target.value)}
              className="h-9 w-32"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Button
              type="button"
              variant={activePreset === 10 ? "default" : "outline"}
              size="sm"
              onClick={() => applyPreset(10)}
            >
              Últimos 10
            </Button>
            <Button
              type="button"
              variant={activePreset === 30 ? "default" : "outline"}
              size="sm"
              onClick={() => applyPreset(30)}
            >
              Últimos 30
            </Button>
            <Button
              type="button"
              variant={activePreset === 50 ? "default" : "outline"}
              size="sm"
              onClick={() => applyPreset(50)}
            >
              Últimos 50
            </Button>
            <Button
              type="button"
              variant={activePreset === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => applyPreset("all")}
            >
              Todos
            </Button>
          </div>
          {(hasFilters || activePreset) && (
            <Button type="button" variant="ghost" size="sm" onClick={clearFilters}>
              <X className="mr-1 h-3.5 w-3.5" /> Limpar
            </Button>
          )}
        </div>
        {availableRange && (
          <div className="text-[11px] text-muted-foreground">
            Faixa disponível: #{availableRange.min} – #{availableRange.max} •{" "}
            {filteredDraws.length} concurso{filteredDraws.length === 1 ? "" : "s"} no filtro
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (prizeContests.length === 0) {
    return (
      <div className="space-y-4">
        {filtersUI}
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <AlertTriangle className="mb-4 h-10 w-10 text-muted-foreground" />
            <h3 className="font-serif text-lg">
              {hasFilters ? "Nenhum prêmio no intervalo" : "Nenhum jogo premiado ainda"}
            </h3>
            <p className="mt-1 max-w-md text-sm text-muted-foreground">
              {hasFilters
                ? `Nenhum jogo deste bolão entrou em faixa de prêmio nos ${filteredDraws.length} concurso${filteredDraws.length === 1 ? "" : "s"} selecionados.`
                : `Este bolão foi conferido contra os últimos ${drawsByContest.length} concursos da loteria, mas nenhum jogo entrou em faixa de prêmio até o momento.`}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {filtersUI}
      <Card className="border-primary/30 bg-lt-gold-bg">
        <CardContent className="flex items-center gap-4 pt-6">
          <Trophy className="h-8 w-8 text-primary" />
          <div>
            <div className="font-serif text-lg">
              {totalWinners} {totalWinners === 1 ? "jogo premiado" : "jogos premiados"} em{" "}
              {prizeContests.length} {prizeContests.length === 1 ? "concurso" : "concursos"}
            </div>
            <div className="text-xs text-muted-foreground">
              {hasFilters
                ? `Intervalo: ${filteredDraws.length} concursos analisados`
                : `Análise dos últimos ${drawsByContest.length} concursos`}
            </div>
          </div>
        </CardContent>
      </Card>

      {prizeContests.map((c) => (
        <Card key={c.contest_number}>
          <CardContent className="space-y-4 pt-6">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-lt-border pb-3">
              <div>
                <div className="font-serif text-xl">Concurso #{c.contest_number}</div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <CalendarDays className="h-3 w-3" />
                  {c.draw_date
                    ? new Date(c.draw_date).toLocaleDateString("pt-BR")
                    : "data indisponível"}
                </div>
              </div>
              <div className="flex flex-wrap gap-1">
                {c.drawn.map((n) => (
                  <span
                    key={n}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-lt-bg2 text-xs font-medium"
                  >
                    {String(n).padStart(2, "0")}
                  </span>
                ))}
              </div>
            </div>

            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              {c.winners.length} {c.winners.length === 1 ? "jogo premiado" : "jogos premiados"}
            </div>

            <div className="space-y-2">
              {c.winners.map((w) => {
                const matchedSet = new Set(w.matched);
                return (
                  <div
                    key={w.position}
                    className="flex flex-wrap items-center gap-3 rounded-md border border-lt-border bg-lt-bg2 p-2.5 text-[13px]"
                  >
                    <span className="w-12 text-xs text-muted-foreground">
                      #{String(w.position).padStart(3, "0")}
                    </span>
                    <div className="flex flex-1 flex-wrap gap-1">
                      {w.numbers.map((n) => {
                        const hit = matchedSet.has(n);
                        return (
                          <span
                            key={n}
                            className={cn(
                              "inline-flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-medium",
                              hit
                                ? "bg-primary text-primary-foreground"
                                : "bg-primary/10 text-primary/60",
                            )}
                          >
                            {String(n).padStart(2, "0")}
                          </span>
                        );
                      })}
                    </div>
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-primary-foreground">
                      <Trophy className="h-3 w-3" />
                      {tierLabel(lotterySlug, w.tier)} • {w.hits} acertos
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}